import type { CalculationTypesType } from "@/dataProviders/CalculationTypes";
import type { CalculationType } from "@/graphql";

/** Either shape a calculation type arrives in: the list query's row, or a single fetched record. */
export type SelectableCalculationType = CalculationTypesType[0] | CalculationType;

export type CalculationTypeDefaults = {
  considerationPeriod: number | null;
  thresholdLimitWithoutExtra: number | null;
  includedPhases: number[];
  includedPhasesTranslated: string[];
  calculationTypeEnum: number;
};

/**
 * What choosing a calculation type implies for a project.
 *
 * The phases matter beyond their own step: Transport, Installation and Energy use are in the wizard
 * only when A4, A5 and B6 respectively are included, so this derivation decides how long the flow is.
 * Which is why it is shared — the Calculation prerequisites step and the modal that opens the wizard
 * both need the answer, and a second copy of it would be a second opinion on the step list.
 *
 * Pure, and returns nulls rather than defaults, because "the calculation type says nothing about the
 * consideration period" and "the consideration period is null" are different claims: the step keeps
 * the project's current value in the first case, which only the caller can know.
 */
export function calculationTypeDefaults(calculationType: SelectableCalculationType): CalculationTypeDefaults {
  return {
    considerationPeriod: calculationType.considerationPeriod ?? null,
    thresholdLimitWithoutExtra: calculationType.threshold ?? null,
    includedPhases: calculationType.stages?.map((stage) => stage.stage.id) ?? [],
    includedPhasesTranslated: calculationType.stages?.map((stage) => stage.stage.label) ?? [],
    calculationTypeEnum: calculationType.id,
  };
}
